import React, { useEffect, useState } from 'react';
import { useOpenLayersContext } from '@ais-ui/react-openlayers';
import { usePopper } from 'react-popper';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
} from '@ais-ui/ui-core';

interface LayerMouseOverEvent extends CustomEvent {
  detail: {
    properties: Record<string, any>;
    position: {
      x: number;
      y: number;
    };
  };
}

interface LayerMouseOutEvent extends CustomEvent {
  detail: {
    previousFeatureId: string;
  };
}

interface LayerPopoverProps {
  properties: Record<string, any>;
  position: { x: number; y: number };
}

const LayerPopover = ({ properties, position }: LayerPopoverProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const name = properties.name || 'Layer Details';

  // Define which fields to show for each layer type
  const getDisplayFields = (props: Record<string, any>) => {
    const type = props.type;

    if (type === 'port') {
      return {
        country: props.country,
        locode: props.locode,
        ...(props.latitude &&
          props.longitude && {
            position: `${Number(props.latitude).toFixed(4)}, ${Number(
              props.longitude
            ).toFixed(4)}`,
          }),
      };
    }

    if (type === 'source-identifier') {
      return {
        ...(props.pssMmsi !== -1 && props.pssMmsi && { MMSI: props.pssMmsi }),
        ...(props.sourceTag && { 'Source Tag': props.sourceTag }),
        ...(props.countryMid && { 'Country MID': props.countryMid }),
        ...(props.regionId && { 'Region ID': props.regionId }),
        ...(props.covEnabled !== undefined && {
          'Coverage Enabled': props.covEnabled ? 'Yes' : 'No',
        }),
        ...(props.createdTimeUtc && {
          Created: new Date(props.createdTimeUtc).toLocaleDateString(),
        }),
      };
    }

    // For other layer types, show filtered properties (existing behavior)
    return Object.entries(props)
      .filter(
        ([key, value]) =>
          !key.startsWith('_') &&
          key !== 'geometry' &&
          key !== 'name' &&
          value !== undefined &&
          value !== ''
      )
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
  };

  const formattedProperties = getDisplayFields(properties);

  const [virtualReference] = useState(() => ({
    getBoundingClientRect: () =>
      ({
        top: position.y,
        left: position.x,
        bottom: position.y,
        right: position.x,
        width: 0,
        height: 0,
      } as DOMRect),
  }));

  const { styles } = usePopper(virtualReference, popperElement, {
    strategy: 'fixed',
    placement: 'right',
  });

  const renderValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  if (!position) return null;

  return (
    <Card
      ref={setPopperElement}
      style={styles.popper}
      className="mx-6 p-0 min-w-[128px]"
    >
      <CardHeader className="p-2">
        <CardTitle className="text-sm">{name}</CardTitle>
      </CardHeader>

      <CardContent className="pt-0 px-3 pb-2">
        <Separator />
        <div className="space-y-1">
          {Object.entries(formattedProperties).map(([key, value]) => (
            <div
              key={key}
              className="grid grid-cols-[1fr_auto] gap-2 max-w-[12rem]"
            >
              <p className="text-xs font-medium flex-none text-left max-w-[6rem]">
                {key}
              </p>
              <p className="text-xs text-muted-foreground flex-grow text-right">
                {renderValue(value) || '-'}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const LayerHover = () => {
  const { map } = useOpenLayersContext();
  const [hoverState, setHoverState] = useState<{
    properties: Record<string, any>;
    position: { x: number; y: number };
  } | null>(null);

  useEffect(() => {
    const handleMouseOver = (event: Event) => {
      const { properties, position } = (event as LayerMouseOverEvent).detail;
      setHoverState({ properties, position });
    };

    const handleMouseOut = () => {
      setHoverState(null);
    };

    const mapElement = map.getTargetElement();

    mapElement?.addEventListener('layer-mouseover', handleMouseOver);
    mapElement?.addEventListener('layer-mouseout', handleMouseOut);

    return () => {
      mapElement?.removeEventListener('layer-mouseover', handleMouseOver);
      mapElement?.removeEventListener('layer-mouseout', handleMouseOut);
    };
  }, [map]);

  if (!hoverState) {
    return null;
  }

  return (
    <LayerPopover
      properties={hoverState.properties}
      position={hoverState.position}
    />
  );
};
